import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertCircle, Loader2, ChevronDown, ChevronRight, Clock, User, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PermissionEditor } from '../PermissionEditor';
import { DeleteRoleDialog } from './DeleteRoleDialog';
import { useCheckRoleIdUnique, useRoleChangelog } from '../../hooks/useRoles';
import type { Role, Permission, UpdateRoleInput, ChangelogEntry } from '../../types';

/**
 * Validate that a permission string has a valid format
 * Format: model[conditions].action[.field[conditions]]
 */
function isValidPermission(permission: Permission): boolean {
  if (!permission.k || !permission.v) return false;

  const parts = permission.k.split('.');
  if (parts.length < 2) return false;

  // First part should be a model (alphanumeric + underscore)
  const modelPart = parts[0].replace(/\[[^\]]*\]$/, ''); // Remove conditions
  if (!modelPart || !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(modelPart)) return false;

  // Second part should be an action
  const validActions = ['read', 'create', 'modify', 'delete'];
  if (!validActions.includes(parts[1])) return false;

  return true;
}

// =============================================================================
// Changelog Components
// =============================================================================

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

function formatUserName(user: { firstname?: string; lastname?: string } | null): string {
  if (!user) return 'Unknown user';
  const parts = [user.firstname, user.lastname].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Unknown user';
}

function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)';
  if (typeof value === 'boolean') return value ? 'Active' : 'Inactive';
  if (Array.isArray(value)) {
    if (value.length === 0) return '(none)';
    return value.join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function ChangelogItem({ entry, isFirst }: { entry: ChangelogEntry; isFirst: boolean }) {
  const { t } = useTranslation('roles');

  return (
    <div className={cn('relative pl-6 pb-6', !isFirst && 'pt-0')}>
      <div className="absolute left-[9px] top-6 bottom-0 w-0.5 bg-border" />
      <div
        className={cn(
          'absolute left-0 top-1 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center',
          entry.isOriginal
            ? 'bg-primary border-primary'
            : 'bg-background border-border'
        )}
      >
        {entry.isOriginal ? (
          <FileText className="h-2.5 w-2.5 text-primary-foreground" />
        ) : (
          <div className="w-2 h-2 rounded-full bg-border" />
        )}
      </div>
      <div className="bg-muted/30 rounded-lg p-3">
        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{formatTimestamp(entry.timestamp)}</span>
          </div>
          <div className="flex items-center gap-1">
            <User className="h-3 w-3" />
            <span>{formatUserName(entry.user)}</span>
          </div>
        </div>
        {entry.isOriginal ? (
          <div className="text-sm text-muted-foreground italic">
            {t('changelog.created')}
          </div>
        ) : entry.changes && entry.changes.length > 0 ? (
          <div className="space-y-0.5">
            {entry.changes.map((change, index) => (
              <div key={`${change.field}-${index}`} className="text-sm py-1">
                <span className="font-medium text-foreground">{change.field}:</span>{' '}
                {change.old !== undefined && (
                  <>
                    <span className="text-red-600 line-through">{formatFieldValue(change.old)}</span>{' '}
                  </>
                )}
                {change.new !== undefined && (
                  <span className="text-green-600">{formatFieldValue(change.new)}</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground italic">
            {t('changelog.updated')}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// RoleFormModal
// =============================================================================

interface RoleFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: Role;
  onSave: (data: UpdateRoleInput) => Promise<void>;
  onDelete?: () => Promise<void>;
  isSubmitting?: boolean;
}

export function RoleFormModal({
  open,
  onOpenChange,
  role,
  onSave,
  onDelete,
  isSubmitting,
}: RoleFormModalProps) {
  const { t } = useTranslation('roles');
  const [roleId, setRoleId] = useState(role.roleId);
  const [permissions, setPermissions] = useState<Permission[]>(role.permissions || []);
  const [roleIdError, setRoleIdError] = useState<string | null>(null);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);

  const checkUniqueMutation = useCheckRoleIdUnique();
  const { data: changelogData, isLoading: changelogLoading, error: changelogError } = useRoleChangelog(
    showChangelog ? role._id : undefined
  );

  // Reset form when role changes
  useEffect(() => {
    setRoleId(role.roleId);
    setPermissions(role.permissions || []);
    setRoleIdError(null);
    setPermissionsError(null);
    setShowChangelog(false);
  }, [role]);

  const validateRoleId = async (): Promise<boolean> => {
    if (!roleId.trim()) {
      setRoleIdError(t('form.roleIdRequired'));
      return false;
    }

    // Only check uniqueness if roleId changed
    if (roleId !== role.roleId) {
      const isUnique = await checkUniqueMutation.mutateAsync({
        roleId,
        excludeId: role._id,
      });

      if (!isUnique) {
        setRoleIdError(t('form.roleIdNotUnique'));
        return false;
      }
    }

    setRoleIdError(null);
    return true;
  };

  const validatePermissions = (): boolean => {
    // Check that all permissions have valid format
    const invalidPermissions = permissions.filter((p) => !isValidPermission(p));
    if (invalidPermissions.length > 0) {
      setPermissionsError(t('form.invalidPermissions', 'Some permissions have invalid format'));
      return false;
    }
    setPermissionsError(null);
    return true;
  };

  const handleSave = async () => {
    const isRoleIdValid = await validateRoleId();
    const isPermissionsValid = validatePermissions();

    if (!isRoleIdValid || !isPermissionsValid) return;

    const data: UpdateRoleInput = {};

    // Only include changed fields
    if (roleId !== role.roleId) {
      data.roleId = roleId;
    }

    // Only include permissions if changed
    const original = role.permissions || [];
    const changed =
      permissions.length !== original.length ||
      permissions.some((p, i) => p.k !== original[i].k || p.v !== original[i].v);
    if (changed) {
      data.permissions = permissions;
    }

    await onSave(data);
  };

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete();
      setShowDeleteDialog(false);
      onOpenChange(false);
    }
  };

  const changelog = changelogData?.changelog || [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('modal.editTitle')}: {role.roleId}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Role ID */}
            <div className="space-y-2">
              <Label htmlFor="roleId">{t('form.roleId')}</Label>
              <Input
                id="roleId"
                value={roleId}
                onChange={(e) => {
                  setRoleId(e.target.value);
                  setRoleIdError(null);
                }}
                placeholder={t('form.roleIdPlaceholder')}
                disabled={isSubmitting}
              />
              {roleIdError && (
                <div className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {roleIdError}
                </div>
              )}
            </div>

            {/* Permissions */}
            <PermissionEditor
              permissions={permissions}
              onChange={(newPerms) => {
                setPermissions(newPerms);
                setPermissionsError(null);
              }}
              disabled={isSubmitting}
            />
            {permissionsError && (
              <div className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                {permissionsError}
              </div>
            )}

            {/* Changelog Section */}
            <div className="border rounded-lg">
              <button
                type="button"
                className="flex items-center gap-2 w-full p-3 text-sm font-medium hover:bg-muted/50 rounded-lg"
                onClick={() => setShowChangelog(!showChangelog)}
              >
                {showChangelog ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                {t('changelog.title')}
              </button>
              {showChangelog && (
                <div className="px-3 pb-3">
                  {changelogLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : changelogError ? (
                    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                      <AlertCircle className="h-8 w-8 mb-2 text-red-500" />
                      <span className="text-sm text-red-600">
                        {changelogError instanceof Error ? changelogError.message : t('toast.unexpectedError')}
                      </span>
                    </div>
                  ) : changelog.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                      <FileText className="h-8 w-8 mb-2" />
                      <span className="text-sm">{t('changelog.empty')}</span>
                    </div>
                  ) : (
                    <ScrollArea className="h-[300px] pr-4">
                      <div className="space-y-0">
                        {changelog.map((entry, index) => (
                          <ChangelogItem
                            key={entry._id || index}
                            entry={entry}
                            isFirst={index === 0}
                          />
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <div>
              {onDelete && (
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={isSubmitting}
                >
                  {t('actions.delete')}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {t('actions.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t('actions.save')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteRoleDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        roleName={role.roleId}
        onConfirm={handleDelete}
        isDeleting={isSubmitting}
      />
    </>
  );
}
